import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { PremiumForSaveResponse, getPremiumForSave } from 'core/application/expenses/getPremiumForSave'
import changePremiumEndDate from 'core/application/expenses/premiumExpenses/ChangePremiumEndDate'

import global from '../../../../common'
import { Skeleton } from '../../../../common/components'
import { DatePicker } from '../../../../common/components/dataEntry/formik'
import Modal from '../../../../common/components/feedback/Modal'
import notification from '../../../../common/components/feedback/Notification'
import { toDateWithoutTimeZone } from '../../../../common/dateFormatters'
import useQueryRequest from '../../../../common/hooks/useQueryRequest'

type ChangeEndDateModalProps = {
  recurringExpenseStartDate: string
  premiumId: string
  expenseTypeId: number
  planPeriodId: string
  onCancel?: () => void
  onPremiumUpdated?: (premiumId: string) => void
}

const ChangeEndDateModal: React.FC<ChangeEndDateModalProps> = (props) => {
  const { t: translate } = useTranslation()

  const [visible, setVisible] = useState<boolean>()
  const [isValidForm, setIsValidForm] = useState<boolean>()
  const [formValues, setFormValues] = useState(defaultChangeEndDateFormValue)
  const [sendCommand, isCommandLoading] = global.hooks.useCommandRequest()
  const [premiumForSave] = useQueryRequest<PremiumForSaveResponse>(null, () =>
    getPremiumForSave(props.planPeriodId, props.expenseTypeId, props.premiumId),
  )

  const onFormSubmit = async () => {
    const result = await sendCommand(() => changePremiumEndDate(props.premiumId, formValues.policyEndDate))

    if (result.completed) {
      notification.open({
        type: 'success',
        message: 'Insurance Policy End Date was changed successfully ',
      })
      setVisible(false)
      if (props.onPremiumUpdated) props.onPremiumUpdated(props.premiumId)
    }
  }

  useEffect(() => {
    setVisible(true)
  }, [])

  return (
    <Modal
      className={'invite-perks-employees-modal-wrap'}
      onCancel={() => {
        if (props.onCancel) {
          props.onCancel()
        }
      }}
      onOk={() => onFormSubmit}
      visible={visible}
      title="Change your policy's end date"
      footer={
        <div>
          <global.components.general.Button
            onClick={() => {
              if (props.onCancel) {
                props.onCancel()
              }
            }}
          >
            {translate('general.cancel')}
          </global.components.general.Button>
          {!isValidForm ? (
            <ChangeEndDateButton
              loading={isCommandLoading}
              disable={isCommandLoading || !isValidForm}
              onClicked={onFormSubmit}
            />
          ) : (
            <ChangeEndDateButton
              loading={isCommandLoading}
              disable={isCommandLoading || !isValidForm}
              onClicked={onFormSubmit}
            />
          )}
        </div>
      }
    >
      <Formik
        validationSchema={formValidator(translate)}
        initialValues={formValues}
        onSubmit={() => {}}
        validate={(values) => {
          setIsValidForm(formValidator(translate).isValidSync(values))
          setFormValues(values)
          return {}
        }}
      >
        {(formik) => (
          <Skeleton loading={!premiumForSave}>
            {premiumForSave && (
              <form onSubmit={formik.handleSubmit} autoComplete="off">
                <p className={'mb-24'}>
                  Enter when your policy ends (like when you purchased a replacement policy for the next year)
                </p>

                <div>
                  <label className={'mb-8 d-block'}>* Policy end date</label>

                  <DatePicker
                    placeholder="Policy end date"
                    name="policyEndDate"
                    id="policyEndDate"
                    noStyles
                    disabledDate={(d) =>
                      !d ||
                      d.isAfter(toDateWithoutTimeZone(premiumForSave.maxPurchasedDate), 'day') ||
                      d.isBefore(toDateWithoutTimeZone(props.recurringExpenseStartDate), 'day')
                    }
                  />
                </div>
              </form>
            )}
          </Skeleton>
        )}
      </Formik>
    </Modal>
  )
}

type ChangeEndDateButtonProps = {
  disable?: boolean
  loading?: boolean
  onClicked?: () => void
}

const ChangeEndDateButton = (props: ChangeEndDateButtonProps) => {
  return (
    <global.components.general.Button
      onClick={props.onClicked}
      type="primary"
      disabled={props.disable}
      loading={props.loading}
      className="ml-8"
    >
      Save
    </global.components.general.Button>
  )
}

export const defaultChangeEndDateFormValue = {
  policyEndDate: null,
}

const formValidator = (translate: any) =>
  Yup.object({
    policyEndDate: Yup.date().required(translate('general.required')).typeError(''),
  })

export default ChangeEndDateModal
